<script lang="ts" setup>
import TranslatedState from '@/components/common/TranslatedState.vue';
import InitiativeShortForm from '@/components/initiative/InitiativeShortForm.vue';
import { Initiative, getShortStatus } from '@/models/initiative.model';
import { useEvents, Event as EventType } from '@/services/events.composable';
import { Actions, Mutations, ShiftCMSState } from '@/store';
import { ContentStoreActions, ContentStoreState } from '@/stores/content.store';
import { computed, markRaw, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';

const store = useStore<ShiftCMSState & { cns: ContentStoreState }>();
const { t } = useI18n();
const router = useRouter();
const events = useEvents();

const initiatives = computed<Initiative[]>(() => store.state.cns.initiatives);

function addInitiative() {
  store.commit(Mutations.ShowDrawer, {
    component: markRaw(InitiativeShortForm),
  });
}

function editInitiative(_: Event, { item }: { item: Initiative }) {
  router.push(`/initiative/${item.publicUUID}/edit`);
}

const updateLanguage = async () => {
  store.dispatch(ContentStoreActions.GetInitiativesByContentEditor);
};

onMounted(() => {
  events.on(EventType.LanguageChanged, updateLanguage);
  updateLanguage();
});

onBeforeUnmount(() => {
  events.off(EventType.LanguageChanged, updateLanguage);
});

const headers = [
  {
    title: t('initiative.edit.index'),
    key: 'index',
    width: '2%',
  },
  { title: t('initiative.edit.title'), key: 'title', width: '50%' },
  { title: t('initiative.edit.category'), key: 'categories', width: '25%' },
  { title: t('initiative.edit.sharing'), key: 'options', width: '10%' },
  { title: t('initiative.edit.state'), key: 'translated', sortable: false },
];

function share(evt: Event, item: Initiative) {
  navigator.clipboard.writeText(
    `${document.location.protocol}//${document.location.host}/#/initiative/${item.publicUUID}/edit`,
  );

  store.dispatch(Actions.ShowNotification, {
    message: 'Copied to clipboard',
    type: 'success',
  });
  evt.stopPropagation();
}
</script>
<template>
  <v-container class="mt-16">
    <v-row>
      <v-col cols="9">
        <h1>{{ $t('initiative.list.header') }}</h1>
      </v-col>
      <v-col cols="3">
        <v-btn @click="addInitiative">{{ $t('initiative.list.add') }}</v-btn>
      </v-col>
    </v-row>

    <v-data-table
      density="compact"
      :headers="headers"
      :items="initiatives"
      @click:row.prevent="editInitiative"
      itemsPerPage="25"
      hover
    >
      <template v-slot:item.translated="{ item }">
        <div style="display: flex; flex-direction: row">
          <v-chip
            class="mr-4"
            color="primary"
            density="compact"
            variant="flat"
            >{{
              $t(`initiative.edit.status_${getShortStatus(item, item.status)}`)
            }}</v-chip
          >
          <TranslatedState :item="item" />
          <v-tooltip text="This item is recommended">
            <template v-slot:activator="{ props }">
              <v-icon
                v-bind="props"
                icon="mdi-thumb-up-outline"
                color="primary"
                class="ml-2"
                v-if="!!item.recommended"
              ></v-icon>
            </template>
          </v-tooltip>
          &nbsp;
          <v-tooltip
            :text="
              item.published
                ? 'This item is published'
                : 'This item is not published'
            "
          >
            <template v-slot:activator="{ props }">
              <v-icon
                v-bind="props"
                :icon="item.published ? 'mdi-eye-check' : 'mdi-eye-outline'"
                :color="item.published ? 'green' : 'red'"
              ></v-icon>
            </template>
          </v-tooltip>
        </div>
      </template>
      <template v-slot:item.categories="{ item }">
        {{
          item.categories && item.categories[0]
            ? item.categories[0].title
            : item.mainCategory
            ? item.mainCategory.title
            : ''
        }}
      </template>
      <template v-slot:item.options="{ item }">
        <v-btn
          density="compact"
          color="indigo-lighten-3"
          @click.prevent="share($event, item)"
          >{{ $t('initiative.edit.share') }}</v-btn
        >
      </template>
      <template v-slot:item.title="{ value }">
        {{ value }} <em v-if="!value">[no translation]</em>
      </template>
      <template v-slot:item.index="{ index }">
        {{ index + 1 }}
      </template>
    </v-data-table>
  </v-container>
</template>
